import React, { useState, useEffect } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Switch,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import styled from "styled-components";
import { updateDestinationMeta } from "../../../services/TopDestinationService";
import PermissionGate from "../../controls/auth/PermissionGate";

type FaqItem = { question: string; answer: string };
type LandmarkItem = { name: string; description: string; distance: string };

interface Props {
	open: boolean;
	onClose: () => void;
	destination: any;
	onSaved: () => void;
}

const TabPanel = styled.div`
	padding: 16px 0;
`;

const ItemRow = styled.div`
	display: flex;
	gap: 8px;
	align-items: flex-start;
	margin-bottom: 12px;
	padding: 12px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
`;

const ItemFields = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const ItemActions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

export default function DestinationContentDialog({ open, onClose, destination, onSaved }: Props) {
	const [tab, setTab] = useState(0);
	const [faqContent, setFaqContent] = useState<FaqItem[]>([]);
	const [landmarksContent, setLandmarksContent] = useState<LandmarkItem[]>([]);
	const [editorialContent, setEditorialContent] = useState("");
	const [contentPublished, setContentPublished] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (destination) {
			setFaqContent(destination.faqContent || []);
			setLandmarksContent(destination.landmarksContent || []);
			setEditorialContent(destination.editorialContent || "");
			setContentPublished(destination.contentPublished || false);
			setTab(0);
		}
	}, [destination]);

	const handleSave = async () => {
		setSaving(true);
		try {
			await updateDestinationMeta(destination.id, {
				faqContent,
				landmarksContent,
				editorialContent,
				contentPublished,
			});
			onSaved();
			onClose();
		} catch (err) {
			console.error("Failed to save destination content:", err);
		} finally {
			setSaving(false);
		}
	};

	// FAQ handlers
	const addFaq = () => setFaqContent([...faqContent, { question: "", answer: "" }]);
	const removeFaq = (i: number) => setFaqContent(faqContent.filter((_, idx) => idx !== i));
	const updateFaq = (i: number, field: keyof FaqItem, value: string) => {
		const updated = [...faqContent];
		updated[i] = { ...updated[i], [field]: value };
		setFaqContent(updated);
	};
	const moveFaq = (i: number, dir: -1 | 1) => {
		const j = i + dir;
		if (j < 0 || j >= faqContent.length) return;
		const updated = [...faqContent];
		[updated[i], updated[j]] = [updated[j], updated[i]];
		setFaqContent(updated);
	};

	// Landmark handlers
	const addLandmark = () => setLandmarksContent([...landmarksContent, { name: "", description: "", distance: "" }]);
	const removeLandmark = (i: number) => setLandmarksContent(landmarksContent.filter((_, idx) => idx !== i));
	const updateLandmark = (i: number, field: keyof LandmarkItem, value: string) => {
		const updated = [...landmarksContent];
		updated[i] = { ...updated[i], [field]: value };
		setLandmarksContent(updated);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth>
			<DialogTitle>Edit Content — {destination?.name || destination?.city || destination?.id || ""}</DialogTitle>
			<DialogContent>
				<Tabs
					value={tab}
					onChange={(_e, v) => setTab(v)}
					indicatorColor="primary">
					<Tab label={`FAQ (${faqContent.length})`} />
					<Tab label={`Landmarks (${landmarksContent.length})`} />
					<Tab label="Editorial" />
				</Tabs>

				{tab === 0 && (
					<TabPanel>
						{faqContent.map((faq, i) => (
							<ItemRow key={i}>
								<ItemFields>
									<TextField
										label="Question"
										value={faq.question}
										onChange={(e) => updateFaq(i, "question", e.target.value)}
										fullWidth
										size="small"
										variant="outlined"
									/>
									<TextField
										label="Answer"
										value={faq.answer}
										onChange={(e) => updateFaq(i, "answer", e.target.value)}
										fullWidth
										multiline
										rows={2}
										size="small"
										variant="outlined"
									/>
								</ItemFields>
								<ItemActions>
									<PermissionGate permission="documents.manage">
										<IconButton
											size="small"
											onClick={() => moveFaq(i, -1)}
											disabled={i === 0}
											aria-label="move up">
											<ArrowUpwardIcon fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => moveFaq(i, 1)}
											disabled={i === faqContent.length - 1}
											aria-label="move down">
											<ArrowDownwardIcon fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => removeFaq(i)}
											aria-label="remove faq">
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</PermissionGate>
								</ItemActions>
							</ItemRow>
						))}
						<PermissionGate permission="documents.manage">
							<Button
								startIcon={<AddIcon />}
								onClick={addFaq}
								color="primary">
								Add FAQ
							</Button>
						</PermissionGate>
					</TabPanel>
				)}

				{tab === 1 && (
					<TabPanel>
						{landmarksContent.map((lm, i) => (
							<ItemRow key={i}>
								<ItemFields>
									<TextField
										label="Name"
										value={lm.name}
										onChange={(e) => updateLandmark(i, "name", e.target.value)}
										fullWidth
										size="small"
										variant="outlined"
									/>
									<TextField
										label="Description"
										value={lm.description}
										onChange={(e) => updateLandmark(i, "description", e.target.value)}
										fullWidth
										multiline
										rows={2}
										size="small"
										variant="outlined"
									/>
									<TextField
										label="Distance (e.g., '5 min walk')"
										value={lm.distance}
										onChange={(e) => updateLandmark(i, "distance", e.target.value)}
										fullWidth
										size="small"
										variant="outlined"
									/>
								</ItemFields>
								<ItemActions>
									<PermissionGate permission="documents.manage">
										<IconButton
											size="small"
											onClick={() => removeLandmark(i)}
											aria-label="remove landmark">
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</PermissionGate>
								</ItemActions>
							</ItemRow>
						))}
						<PermissionGate permission="documents.manage">
							<Button
								startIcon={<AddIcon />}
								onClick={addLandmark}
								color="primary">
								Add Landmark
							</Button>
						</PermissionGate>
					</TabPanel>
				)}

				{tab === 2 && (
					<TabPanel>
						<TextField
							label="Editorial Content (HTML)"
							value={editorialContent}
							onChange={(e) => setEditorialContent(e.target.value)}
							fullWidth
							multiline
							rows={12}
							variant="outlined"
						/>
						<Typography
							variant="caption"
							color="textSecondary"
							style={{ marginTop: 4, display: "block" }}>
							{editorialContent.length} characters
						</Typography>
					</TabPanel>
				)}
			</DialogContent>
			<DialogActions style={{ justifyContent: "space-between", padding: "8px 24px" }}>
				<FormControlLabel
					control={
						<PermissionGate
							permission="documents.manage"
							fallback={
								<Switch
									checked={contentPublished}
									color="primary"
									disabled
								/>
							}>
							<Switch
								checked={contentPublished}
								onChange={(e) => setContentPublished(e.target.checked)}
								color="primary"
							/>
						</PermissionGate>
					}
					label="Content Published"
				/>
				<div>
					<Button
						onClick={onClose}
						style={{ marginRight: 8 }}>
						Cancel
					</Button>
					<PermissionGate permission="documents.manage">
						<Button
							onClick={() => void handleSave()}
							color="primary"
							variant="contained"
							disabled={saving}>
							{saving ? "Saving..." : "Save"}
						</Button>
					</PermissionGate>
				</div>
			</DialogActions>
		</Dialog>
	);
}
